import { action, computed, observable, runInAction, makeObservable } from "mobx";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { FOLDERS_getProjectFolderMetadataOverview } from "General/APIQueryParams";
import { projectsDataStore } from "Stores";
import type { FilterConfig } from "Components/ScrollableTable/ScrollableTable";

type DefinitionsFullResponse = API_Metadata[] | { data?: API_Metadata[]; definitions?: API_Metadata[] };

/** GET …/folders/metadata-overview — JSON array or optional wrapper (see Swagger). */
export type FolderMetadataOverviewResponse = FolderMetadataOverviewEntry[] | { data?: FolderMetadataOverviewEntry[] };

/** One row from the folder metadata overview API (matches backend FolderMetadataOverviewEntry). */
export interface FolderMetadataOverviewEntry {
  folderId: number;
  folderPath: string;
  metadataDefinitionId: number;
  metadataName: string;
  defaultwert: string;
  /**
   * false when includeInherited=true and the value comes from an ancestor folder.
   * If omitted in JSON, treat as true (same default as backend bean / direct-only endpoint).
   */
  direct?: boolean;
  sourceFolderId?: number;
  sourceFolderPath?: string;
  /** Joined client-side from definitionsfull when overview rows are loaded; not from the overview API. */
  definition?: API_Metadata;
}

export type FolderMetadataSortBy =
  | "folderPath"
  | "metadataName"
  | "defaultwert"
  | "value"
  | "source"
  | "sourceFolderPath"
  | "definitionActive"
  | "definitionShow"
  | "definitionType"
  | "definitionRequired"
  | "definitionFormat";
export type FolderMetadataSortOrder = "ASC" | "DESC";

export class FolderMetadataOverviewStore {
  /**
   * Raw rows from GET …/folders/metadata-overview only (no client-side `definition` join).
   * Joined rows for the UI are {@link FolderMetadataOverviewStore.data} (computed).
   */
  overviewRows: FolderMetadataOverviewEntry[] = [];
  /** Project metadata definitions (`definitionsfull`) for column display and client-side filters. */
  metadataDefinitions: API_Metadata[] = [];
  loading = false;
  error: string | null = null;
  /**
   * When true, API returns direct + inherited rows. Default true until filters narrow the load.
   */
  includeInherited = true;
  /** Case-insensitive match on folder path, source path (inherited), and metadata name (API: multipleFieldSearch). */
  multipleFieldSearch = "";
  /** Multiple metadata names to filter (OR). Empty = no filter. */
  metadataNames: string[] = [];
  metadataValue = "";
  sortBy: FolderMetadataSortBy = "folderPath";
  sortOrder: FolderMetadataSortOrder = "ASC";

  constructor() {
    makeObservable(this, {
      overviewRows: observable,
      metadataDefinitions: observable,
      data: computed,
      loading: observable,
      error: observable,
      includeInherited: observable,
      multipleFieldSearch: observable,
      metadataNames: observable,
      metadataValue: observable,
      sortBy: observable,
      sortOrder: observable,
      definitionLookup: computed,
      metadataFilterOptions: computed,
      fetch: action.bound,
      fetchMetadataDefinitions: action.bound,
      fetchDefinitionsAndOverview: action.bound,
      setIncludeInherited: action.bound,
      setMultipleFieldSearch: action.bound,
      setFilters: action.bound,
      setSort: action.bound,
      reset: action.bound,
    });
  }

  get data(): FolderMetadataOverviewEntry[] {
    return this.attachDefinitionsToRows(this.overviewRows);
  }

  private static stripDefinitionFromRow(row: FolderMetadataOverviewEntry): FolderMetadataOverviewEntry {
    const copy = { ...row };
    delete copy.definition;
    return copy;
  }

  get definitionLookup(): { byId: Map<number, API_Metadata>; byKey: Map<string, API_Metadata> } {
    const byId = new Map<number, API_Metadata>();
    const byKey = new Map<string, API_Metadata>();
    const list = Array.isArray(this.metadataDefinitions) ? this.metadataDefinitions : [];
    for (const d of list) {
      const item = d as API_Metadata & { name?: string };
      const key = item.key ?? item.name ?? "";
      let defIdNum = Number.NaN;
      if (item.definitionId != null) {
        defIdNum = Number(item.definitionId);
      } else if (item.id != null) {
        defIdNum = Number(item.id);
      }
      if (!Number.isNaN(defIdNum)) {
        byId.set(defIdNum, item);
      }
      if (key) byKey.set(key, item);
    }
    return { byId, byKey };
  }

  private attachDefinitionsToRows(rows: FolderMetadataOverviewEntry[]): FolderMetadataOverviewEntry[] {
    return rows.map((row) => ({
      ...row,
      definition: this.getDefinitionForEntry(row.metadataDefinitionId, row.metadataName),
    }));
  }

  getDefinitionForEntry(metadataDefinitionId: number, metadataName: string): API_Metadata | undefined {
    const { byId, byKey } = this.definitionLookup;
    const idText = String(metadataDefinitionId ?? "").trim();
    const idNum = Number(idText);
    if (!Number.isNaN(idNum)) {
      const byDefId = byId.get(idNum);
      if (byDefId) {
        return byDefId;
      }
    }
    // Fallback for unexpected wire-shapes where ID fields are strings or not normalized.
    const byLinearId = this.metadataDefinitions.find((item) => {
      const candidate = item.definitionId ?? item.id;
      return candidate != null && String(candidate).trim() === idText;
    });
    if (byLinearId) {
      return byLinearId;
    }
    const nameStr = metadataName == null ? "" : String(metadataName);
    return (
      byKey.get(nameStr) ??
      (nameStr ? byKey.get(nameStr.trim()) : undefined) ??
      (nameStr
        ? this.metadataDefinitions.find((item) => {
            const candidateName = (item.key ?? (item as { name?: string }).name ?? "").trim();
            return candidateName !== "" && candidateName === nameStr.trim();
          })
        : undefined)
    );
  }

  /**
   * Options for the metadata name column filter: definitions from the project plus names
   * present in loaded overview rows.
   */
  get metadataFilterOptions(): FilterConfig[] {
    const byValue = new Map<string, string>();
    const list = Array.isArray(this.metadataDefinitions) ? this.metadataDefinitions : [];
    for (const d of list) {
      const item = d as API_Metadata & { name?: string };
      const name = item.key ?? item.name ?? "";
      let fromDefinitionId = "";
      if (item.definitionId != null) {
        fromDefinitionId = `Metadata (${item.definitionId})`;
      }
      const label = name || item.description || fromDefinitionId || String(item.id ?? "");
      if (name || label) {
        const value = name || String(item.definitionId ?? item.id ?? "");
        if (!byValue.has(value)) byValue.set(value, label || name);
      }
    }
    const overviewNames = this.overviewRows?.map((e) => e.metadataName) ?? [];
    for (const name of new Set(overviewNames)) {
      if (!name) {
        continue;
      }
      if (byValue.has(name)) {
        continue;
      }
      byValue.set(name, name);
    }
    return Array.from(byValue.entries())
      .filter(([v]) => v !== "")
      .map(([value, text]) => ({ value, text }));
  }

  async fetchMetadataDefinitions(): Promise<void> {
    const projectId = projectsDataStore.projectId;
    if (!projectId) {
      runInAction(() => {
        this.metadataDefinitions = [];
      });
      return;
    }
    try {
      const raw = await API.getData<DefinitionsFullResponse>(API_URL.METADATA.getDefinitionsFull(projectId));
      let defs: API_Metadata[] = [];
      if (Array.isArray(raw)) {
        defs = raw;
      } else if (raw && typeof raw === "object") {
        const r = raw as { data?: unknown; definitions?: unknown; items?: unknown; metadata?: unknown };
        if (Array.isArray(r.data)) defs = r.data as API_Metadata[];
        else if (Array.isArray(r.definitions)) defs = r.definitions as API_Metadata[];
        else if (Array.isArray(r.items)) defs = r.items as API_Metadata[];
        else if (Array.isArray(r.metadata)) defs = r.metadata as API_Metadata[];
      }
      runInAction(() => {
        this.metadataDefinitions = defs;
      });
    } catch {
      runInAction(() => {
        this.metadataDefinitions = [];
      });
    }
  }

  /**
   * Loads definitions first, then overview rows, so Active/Show cells always resolve definitionsfull
   * (parallel loads could paint overview before {@link metadataDefinitions} was populated → all "—").
   */
  async fetchDefinitionsAndOverview(): Promise<void> {
    await this.fetchMetadataDefinitions();
    await this.fetch();
  }

  private static readonly SERVER_SORT_KEYS = [
    "folderPath",
    "metadataName",
    "defaultwert",
    "source",
    "sourceFolderPath",
  ] as const;

  private static overviewQueryString(params: FOLDERS_getProjectFolderMetadataOverview): string {
    const q = new URLSearchParams();
    if (params.includeInherited) {
      q.set("includeInherited", "true");
    }
    if (params.multipleFieldSearch) {
      q.set("multipleFieldSearch", params.multipleFieldSearch);
    }
    for (const name of params.metadataName ?? []) {
      const trimmed = name.trim();
      if (trimmed) {
        q.append("metadataName", trimmed);
      }
    }
    if (params.metadataValue) {
      q.set("metadataValue", params.metadataValue);
    }
    if (params.sortBy) {
      q.set("sortBy", params.sortBy);
    }
    if (params.sortOrder) {
      q.set("sortOrder", params.sortOrder);
    }
    return q.toString();
  }

  get url(): string {
    let sortBy: string;
    if (this.sortBy === "value") {
      sortBy = "defaultwert";
    } else if (
      FolderMetadataOverviewStore.SERVER_SORT_KEYS.includes(
        this.sortBy as (typeof FolderMetadataOverviewStore.SERVER_SORT_KEYS)[number]
      )
    ) {
      sortBy = this.sortBy;
    } else {
      sortBy = "folderPath";
    }
    const path = API_URL.FOLDERS.getProjectFolderMetadataOverview(projectsDataStore.projectId);
    const params: FOLDERS_getProjectFolderMetadataOverview = {
      includeInherited: this.includeInherited ? true : undefined,
      multipleFieldSearch: this.multipleFieldSearch || undefined,
      metadataName: this.metadataNames.length ? this.metadataNames : undefined,
      metadataValue: this.metadataValue || undefined,
      sortBy,
      sortOrder: this.sortOrder,
    };
    const qs = FolderMetadataOverviewStore.overviewQueryString(params);
    return qs ? `${path}?${qs}` : path;
  }

  setIncludeInherited(value: boolean): void {
    this.includeInherited = value;
  }

  async fetch(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const raw = await API.getData<FolderMetadataOverviewResponse>(this.url);
      let list: FolderMetadataOverviewEntry[];
      if (Array.isArray(raw)) {
        list = raw;
      } else if (Array.isArray(raw?.data)) {
        list = raw.data;
      } else {
        list = [];
      }
      runInAction(() => {
        this.overviewRows = list.map(FolderMetadataOverviewStore.stripDefinitionFromRow);
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load folder metadata";
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  setMultipleFieldSearch(multipleFieldSearch: string): void {
    this.multipleFieldSearch = multipleFieldSearch;
  }

  setFilters(metadataNames: string[], metadataValue: string): void {
    this.metadataNames = metadataNames;
    this.metadataValue = metadataValue;
  }

  setSort(sortBy: FolderMetadataSortBy, sortOrder: FolderMetadataSortOrder): void {
    this.sortBy = sortBy;
    this.sortOrder = sortOrder;
  }

  reset(): void {
    this.overviewRows = [];
    this.metadataDefinitions = [];
    this.includeInherited = true;
    this.multipleFieldSearch = "";
    this.metadataNames = [];
    this.metadataValue = "";
    this.sortBy = "folderPath";
    this.sortOrder = "ASC";
    this.error = null;
  }
}

export const folderMetadataOverviewStore = new FolderMetadataOverviewStore();
